import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateRangePicker } from "react-date-range";
import { addDays, format, parseISO, startOfDay } from "date-fns";
import moment from "moment";
import { useFlags } from "launchdarkly-react-client-sdk";

import { usePermissions } from "components/Hook";

import { GetAllHolidays } from "store/actions/Holidays";

import { baseUrl } from "util/APIBaseUrl";
import { findNearestSaturday } from "util/utils";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./Daterange.css";

function Daterange({
  months = 2,
  onChange,
  leavesCalender = [],
  setSelectedDateRange = () => {},
  holidaysCalender,
  disabledFutureDates,
  validDates,
  isSwappedLeaves = false,
  onError = () => {},
  disabledDate = null,
  disableHolidays = false,
  allowWeekendSelection = false,
  initialDateRange = null,
  shownDate = null,
}) {
  const { loggedInUser, loggedInUserType } = usePermissions();
  const allHolidays = useSelector((state) => state?.Holidays?.allHolidays);
  const { swappedLeavesRevamp } = useFlags();
  const { startDate: initialStartDate, endDate: initialEndDate } = initialDateRange || {};

  const dispatch = useDispatch();
  const [state, setState] = useState(() => {
    if (initialDateRange && initialStartDate && initialEndDate) {
      return [
        {
          startDate: new Date(initialStartDate),
          endDate: new Date(initialEndDate),
          key: "selection",
        },
      ];
    }

    if (swappedLeavesRevamp && isSwappedLeaves) {
      const nearestSaturday = findNearestSaturday();
      return [
        {
          startDate: nearestSaturday,
          endDate: nearestSaturday,
          key: "selection",
        },
      ];
    }
    return [
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ];
  });

  const isWeekend = (date) => {
    const dayOfWeek = moment(date).day();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const getHolidayName = (day) => {
    if (loggedInUserType === "edge") {
      const holiday = holidaysCalender?.find(
        (leave) =>
          moment(leave?.Date).format("MMM DD, YYYY") === moment(day).format("MMM DD, YYYY") ||
          moment(leave?.EndDate).format("MMM DD, YYYY") === moment(day).format("MMM DD, YYYY")
      );
      return holiday?.Name || null;
    }

    const holiday = allHolidays?.data?.find(
      (leave) =>
        moment(day).isSame(moment(leave?.StartDate.split("T")[0]), "day") ||
        (leave.EndDate &&
          moment(day).isBetween(
            moment(leave?.StartDate.split("T")[0]),
            moment(leave?.EndDate.split("T")[0]),
            null,
            "[]"
          ))
    );
    return holiday?.Name || null;
  };

  const isHolidayFromCalender = (date) => {
    if (!holidaysCalender || !holidaysCalender.length) return false;

    return holidaysCalender.some((holiday) => {
      const { StartDate: holidayStartDate, EndDate: holidayEndDate } = holiday;
      const holidayStart = moment(holidayStartDate);
      const holidayEnd = holidayEndDate ? moment(holidayEndDate) : holidayStart;
      const checkDate = moment(date);

      return checkDate.isBetween(holidayStart, holidayEnd, "day", "[]");
    });
  };

  const isValidSwapDate = (date) => {
    return isWeekend(date) || !!getHolidayName(date);
  };

  const validateSwapDateRange = (startDate, endDate) => {
    if (!swappedLeavesRevamp || !isSwappedLeaves) return { isValid: true, error: "" };

    const dates = [moment(startDate), moment(endDate)].sort((a, b) => a.diff(b));
    const start = dates[0];
    const end = dates[1];

    if (start.isSame(end, "day")) {
      return { isValid: true, error: "" };
    }

    const current = start.clone();

    while (current.isSameOrBefore(end)) {
      const isValid = isValidSwapDate(current.toDate());

      if (!isValid) {
        return {
          isValid: false,
          error: "Swap days should only include holidays or weekends.",
        };
      }
      current.add(1, "day");
    }

    return { isValid: true, error: "" };
  };

  const getDateLabels = (startDate, endDate) => {
    if (!swappedLeavesRevamp || !isSwappedLeaves) return "";

    const start = moment(startDate);
    const end = moment(endDate);
    const current = start.clone();
    const holidays = new Set();
    let hasWeekendWithoutHoliday = false;

    while (current.isSameOrBefore(end)) {
      const holidayName = getHolidayName(current.toDate());
      if (holidayName) {
        holidays.add(holidayName);
      } else if (isWeekend(current.toDate())) {
        hasWeekendWithoutHoliday = true;
      }
      current.add(1, "day");
    }

    const labels = [];
    if (holidays.size > 0) {
      labels.push(...Array.from(holidays));
    }
    if (hasWeekendWithoutHoliday) {
      labels.push("weekend");
    }

    return labels.length > 0 ? `(${labels.join(", ")})` : "";
  };

  const isHolidayIndicator = (day) => {
    if (loggedInUserType === "edge") {
      return (
        holidaysCalender?.find(
          (leave) =>
            moment(leave?.Date).format("MMM DD, YYYY") == moment(day).format("MMM DD, YYYY") ||
            moment(leave?.EndDate).format("MMM DD, YYYY") == moment(day).format("MMM DD, YYYY")
        ) || false
      );
    }

    return (
      allHolidays?.data?.find(
        (leave) =>
          moment(day).isSame(moment(leave?.StartDate.split("T")[0]), "day") ||
          (leave.EndDate &&
            moment(day).isBetween(
              moment(leave?.StartDate.split("T")[0]),
              moment(leave?.EndDate.split("T")[0]),
              null,
              "[]"
            ))
      ) || false
    );
  };

  function customDayContent(day) {
    let leavesIndicator = null;
    let holidaysIndicator = null;

    if (
      leavesCalender.find(
        (leave) => moment(leave?.leaveDate).format("MMM DD, YYYY") == moment(day).format("MMM DD, YYYY")
      )
    ) {
      leavesIndicator = (
        <div
          id="leave-daterange"
          style={{
            height: "5px",
            width: "5px",
            borderRadius: "100%",
            background: "#FFC200",
          }}
        />
      );
    }
    if (isHolidayIndicator(day)) {
      holidaysIndicator = (
        <div
          id="holiday-daterange"
          style={{
            height: "5px",
            width: "5px",
            borderRadius: "100%",
            background: "#1EE3CF",
          }}
        />
      );
    }

    return (
      <div style={{ flexDirection: "column", gap: "13px", marginTop: "-9px" }}>
        <div style={{ gap: "2px", marginTop: "24px" }}>
          {leavesIndicator}
          {holidaysIndicator}
        </div>
        <span className="daterander-date" id="date">
          {format(day, "d")}
        </span>
      </div>
    );
  }

  useEffect(() => {
    if (loggedInUserType != "edge" && loggedInUserType !== "employee")
      dispatch(
        GetAllHolidays({
          request: `${baseUrl}/customer/${loggedInUser?.customerId || loggedInUser?.pendingCustomerId}/publicHolidays`,
          region: "",
          startDate: addDays(new Date(), -365),
          endDate: addDays(new Date(), 365),
        })
      );
  }, [dispatch, loggedInUser?.customerId, loggedInUser?.pendingCustomerId, loggedInUserType]);

  useEffect(() => {
    if (initialDateRange && initialStartDate && initialEndDate) {
      setState([
        {
          startDate: new Date(initialStartDate),
          endDate: new Date(initialEndDate),
          key: "selection",
        },
      ]);
    }
  }, [initialDateRange]);

  const maxDate =
    (validDates && startOfDay(parseISO(validDates.endDate))) || (disabledFutureDates && moment().toDate()) || undefined;

  const isDateDisabled = (date) => {
    if (disabledDate && typeof disabledDate === "function") {
      if (disabledDate(date)) return true;
    }

    if (disableHolidays && isHolidayFromCalender(date)) {
      return true;
    }

    return false;
  };

  return (
    <div
      className={`date-range-container custom-date-picker ${swappedLeavesRevamp && isSwappedLeaves ? "swap-leaves-mode" : ""} ${allowWeekendSelection ? "weekend-selection-enabled" : ""}`}
    >
      <DateRangePicker
        onChange={(item) => {
          const { startDate, endDate } = item.selection;

          setState([item.selection]);

          const start = moment(startDate);
          const end = moment(endDate);
          const daysCount = end.diff(start, "days") + 1;

          if (swappedLeavesRevamp && isSwappedLeaves) {
            const validation = validateSwapDateRange(startDate, endDate);

            if (!validation.isValid) {
              onError(validation.error);
              if (onChange) {
                onChange(item.selection, "", daysCount);
              }
            } else {
              const dateLabels = getDateLabels(startDate, endDate);
              if (onChange) {
                onChange(item.selection, dateLabels, daysCount);
              }
              setSelectedDateRange({
                startDate: moment(startDate).format("MMM DD, YYYY"),
                endDate: moment(endDate).format("MMM DD, YYYY"),
                daysCount,
              });
              onError("");
            }
          } else {
            if (onChange) {
              onChange(item.selection, "", daysCount);
            }
            setSelectedDateRange({
              startDate: moment(startDate).format("MMM DD, YYYY"),
              endDate: moment(endDate).format("MMM DD, YYYY"),
              daysCount,
            });
            onError("");
          }
        }}
        showSelectionPreview={months !== 1}
        editableDateInputs={months !== 1}
        months={months}
        ranges={state}
        dayContentRenderer={customDayContent}
        direction="horizontal"
        fontFamily="var(--font-family-inter)"
        color="var(--temp-color-primitives-primary-800)"
        weekStartsOn={1}
        weekdayDisplayFormat="EEEEEE"
        ariaLabels={{
          dateInput: {
            startDate: "rdr-start-date",
            endDate: "rdr-end-date",
          },
          monthPicker: "rdr-month-date",
          yearPicker: "rdr-year-date",
          prevButton: "rdr-prev-date",
          nextButton: "rdr-next-date",
        }}
        minDate={validDates && new Date(validDates?.startDate)}
        maxDate={maxDate}
        disabledDay={isDateDisabled}
        {...(shownDate ? { shownDate: new Date(shownDate) } : {})}
      />
    </div>
  );
}

export default Daterange;
